import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useRef, useState } from 'react';
import NoteForm, { NoteFormRef, NoteValues } from './NoteForm';
import { INote } from '@nxsys/types/models/note';
import AppDescription from '@nxsys/components/AppDescription';
import { Descriptions } from 'antd';
import { IconDelete } from '@nxsys/components/AppIconSvg';
import moment from 'moment';

type DetailNoteModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onUpdate: (values: NoteValues) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  data: INote | null;
};

const DetailNoteModal: React.FC<DetailNoteModalProps> = ({
  isOpen,
  toggle,
  onUpdate,
  data,
  onDelete,
}: DetailNoteModalProps) => {
  const { messages } = useTypedIntl();

  const formRef = useRef<NoteFormRef>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleUpdateNewNote = async (values: NoteValues) => {
    try {
      setIsSubmitting(true);
      await onUpdate(values);
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNote = async () => {
    try {
      setIsDeleting(true);
      await onDelete(data?.id || '');
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['common.detailNote']}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                loading: isSubmitting,
                disabled: isSubmitting,
              },
            ]
          : [
              {
                variant: 'outlined',
                onClick: handleDeleteNote,
                loading: isDeleting,
                disabled: isDeleting,
                name: messages['common.delete'],
                buttonType: 'primary',
                className: 'btn-delete',
                icon: <IconDelete width={20} height={20} />,
              },
              {
                name: messages['common.edit'],
                variant: 'outlined',
                onClick: () => setIsEditing(true),
              },
            ]
      }
      minHeight="200px"
    >
      {isEditing ? (
        <NoteForm
          formRef={formRef}
          onSubmit={handleUpdateNewNote}
          defaultValues={{
            title: data?.title || '',
            content: data?.content || '',
          }}
        />
      ) : (
        <AppDescription
          column={1}
          labelStyle={{ width: 216 }}
          bordered={false}
          colon={false}
          size="middle"
        >
          <Descriptions.Item label="Title">
            {data?.title || ''}
          </Descriptions.Item>
          <Descriptions.Item label="Content">
            <div dangerouslySetInnerHTML={{ __html: data?.content || '' }} />
          </Descriptions.Item>
          <Descriptions.Item label="Created On">
            {moment(data?.createdAt).format('DD/MM/YYYY')}
          </Descriptions.Item>
        </AppDescription>
      )}
    </AppModalContainer>
  );
};

export default DetailNoteModal;
